import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarCheck2, AlertCircle, BookOpen, Truck } from "lucide-react";
import { getPublicLinkBySlug, submitPublicBooking, getPublicLinkTos, getPublicAvailability, setBookingDeclarations, missingRequired, publicRequiredFields } from "@/lib/api";
import { useDeclarations, useDeclarationsNotice, DeclarationsList } from "@/components/DeclarationsBlock";
import SiteClockLine from "@/components/SiteClockLine";
import { placeTimeLabel, todayInTz } from "@/lib/datetime";
import { bayRules, openStarts } from "@/lib/availability";
import { useTenantBranding, TenantLogo } from "@/components/TenantBrand";
import SiteBroadcastBanners from "@/components/SiteBroadcastBanners";
import { TosMarkdown } from "@/components/TosMarkdown";
import { AuDateInput } from "@/components/AuDateTime";

export default function PublicBooking() {
  const { slug } = useParams();
  const [link, setLink] = useState(null);
  const [tos, setTos] = useState(null); // { tos_markdown, tos_version } | null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const brand = useTenantBranding({ link_slug: slug });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [linkRes, tosRes] = await Promise.all([
        getPublicLinkBySlug(slug),
        getPublicLinkTos(slug),
      ]);
      if (cancelled) return;
      if (linkRes.error) setError(linkRes.error.message);
      else if (!linkRes.data) setError("This booking link is not available.");
      else setLink(linkRes.data);
      const tosRow = Array.isArray(tosRes.data) ? tosRes.data[0] : tosRes.data;
      const tosText = (tosRow?.tos_markdown || "").trim();
      if (tosText && (tosRow?.tos_version || 0) > 0) {
        setTos({ markdown: tosText, version: tosRow.tos_version });
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return <Frame brand={brand}><p className="text-slate-500 text-sm">Loading…</p></Frame>;
  }
  if (error) {
    return (
      <Frame brand={brand}>
        <div className="text-center py-10" data-testid="public-link-error">
          <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-2" />
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </Frame>
    );
  }
  if (submitted) {
    return (
      <Frame brand={brand}>
        <div className="text-center py-8" data-testid="public-booking-success">
          <CalendarCheck2 className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
          <h2 className="text-xl font-semibold">Booking received</h2>
          <p className="text-sm text-slate-600 mt-1">
            Your reference is <strong>{submitted.reference_number}</strong>.
            We&apos;ll be in touch shortly.
          </p>
        </div>
      </Frame>
    );
  }

  return (
    <Frame brand={brand}>
      {link.site_id && <div className="mb-4"><SiteBroadcastBanners siteId={link.site_id} /></div>}
      <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
        <Truck className="h-6 w-6" /> {link.title}
      </h1>
      {link.description && (
        <p className="text-sm text-slate-600 mt-1.5">{link.description}</p>
      )}
      {/* Site info link — opens the per-site rules / driver-info page in a new tab.
          We derive clientSlug from the link slug prefix (e.g. "kcs-merrigum" → "kcs")
          because anon RLS on lbs_clients blocks the direct join. */}
      {link.site_id && (brand?.client_slug || link.client?.slug) && (
        <a
          href={`/p/${brand?.client_slug || link.client?.slug}/site-info/${link.site_id}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="site-info-link"
          className="mt-3 inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 hover:bg-blue-100 transition"
        >
          <BookOpen className="h-4 w-4" />
          <span>Read site rules &amp; driver info</span>
          <span className="text-blue-600 ml-auto">↗</span>
        </a>
      )}
      <BookingForm link={link} tos={tos} onSubmitted={setSubmitted} />
    </Frame>
  );
}

function BookingForm({ link, tos, onSubmitted }) {
  const today = todayInTz(link.site?.timezone || "Australia/Melbourne");
  const [v, setV] = useState({
    direction: link.allowed_directions?.[0] || "inbound",
    date: today,
    start_ms: null,
    bay_id: "",
    duration_mins: 60,
    sender_name: "",
    sender_company: "",
    sender_phone: "",
    sender_email: "",
    receiver_name: "",
    receiver_company: "",
    goods_description: "",
    vehicle_type: "",
    vehicle_registration: "",
    driver_phone: "",
    notes: "",
  });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const declarations = useDeclarations({ slug: link.slug, direction: v.direction });
  const declNotice = useDeclarationsNotice({ slug: link.slug });
  const [declAnswers, setDeclAnswers] = useState({});
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const tenantRequired = usePublicRequired(link.slug);
  const req = (k) => tenantRequired.has(k);

  // Available start times for the chosen date (site timezone, all booking rules applied).
  const [avail, setAvail] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setAvail(null);
    if (v.date) getPublicAvailability(link.slug, v.date).then((a) => { if (!cancelled) setAvail(a || { error: true }); });
    return () => { cancelled = true; };
  }, [link.slug, v.date, reload]);
  const rules = useMemo(() => (avail?.site && avail?.bay
    ? bayRules(avail.site, avail.bay, { bufferEnabled: avail.buffer_enabled }) : null), [avail]);
  const autoAssign = avail?.auto_assign !== false;
  const pickBay = !autoAssign && !!avail?.bays?.length;
  // Start times across the candidate bays. Auto-assign: union of all active bays
  // (server picks the first free one). Otherwise: the carrier's chosen bay only.
  const bayStarts = (a, dateStr, len, bayId) => {
    if (!a?.site || !a?.bays?.length) return [];
    const [year, month, day] = dateStr.split("-").map(Number);
    const out = [];
    for (const bay of a.bays.filter((b) => !bayId || b.id === bayId)) {
      const r = bayRules(a.site, bay, { bufferEnabled: a.buffer_enabled });
      const data = {
        bookings: (a.bookings || []).filter((b) => b.bay_id === bay.id),
        blackouts: (a.blackouts || []).filter((x) => !x.bay_id || x.bay_id === bay.id),
      };
      openStarts({ year, month, day }, r, data, len).forEach((ms) => out.push({ ms, bayId: bay.id }));
    }
    return out.sort((x, y) => x.ms - y.ms);
  };
  const startsFor = (a, dateStr, len, bayId) => [...new Set(bayStarts(a, dateStr, len, bayId).map((x) => x.ms))];
  const [searching, setSearching] = useState(false);
  const findNext = async () => {
    setSearching(true);
    try {
      const base = new Date(`${v.date || today}T00:00:00Z`);
      for (let i = 0; i <= 60; i++) {
        const d = new Date(base.getTime() + i * 86400000).toISOString().slice(0, 10);
        const a = i === 0 && avail?.site ? avail : await getPublicAvailability(link.slug, d);
        const st = bayStarts(a, d, duration, a?.auto_assign === false ? v.bay_id : "");
        if (st.length) {
          setV((s) => ({ ...s, date: d, start_ms: st[0].ms, bay_id: a?.auto_assign === false ? st[0].bayId : s.bay_id }));
          return;
        }
      }
      toast.error("No available times in the next 60 days.");
    } finally {
      setSearching(false);
    }
  };
  const durations = rules ? [0, 1, 2, 3].map((i) => rules.minLen + i * rules.slot) : [60];
  const duration = rules && !durations.includes(v.duration_mins) ? rules.minLen : v.duration_mins;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const starts = useMemo(() => (v.date && (!pickBay || v.bay_id) ? startsFor(avail, v.date, duration, pickBay ? v.bay_id : "") : []), [avail, v.date, duration, pickBay, v.bay_id]);
  const fmtStart = (ms) => new Intl.DateTimeFormat("en-AU", { timeZone: rules?.tz || avail?.site?.timezone || "Australia/Melbourne", hour: "2-digit", minute: "2-digit", hour12: false }).format(ms);

  const submit = async (e) => {
    e.preventDefault();
    if (!v.sender_name && !v.sender_company) {
      toast.error("Please tell us who's sending.");
      return;
    }
    const missingTenant = Object.keys(PUBLIC_FIELD_LABELS).filter((k) => req(k) && !String(v[PUBLIC_VALUE_KEY[k] || k] || "").trim());
    if (missingTenant.length) {
      toast.error(`Still required: ${missingTenant.map((k) => PUBLIC_FIELD_LABELS[k]).join(", ")}`);
      return;
    }
    if (tos && !tosAccepted) {
      toast.error("Please accept the Terms of Service to continue.");
      return;
    }
    if (pickBay && !v.bay_id) {
      toast.error("Please pick a bay.");
      return;
    }
    if (!v.start_ms || !starts.includes(v.start_ms)) {
      toast.error("Please pick one of the available start times.");
      return;
    }
    const unticked = missingRequired(declarations, declAnswers);
    const start = new Date(v.start_ms);
    const end = new Date(start.getTime() + duration * 60 * 1000);
    const payload = {
      direction: v.direction,
      title: link.title,
      scheduled_start: start.toISOString(),
      scheduled_end: end.toISOString(),
      ...(pickBay ? { bay_id: v.bay_id } : {}),
      sender_name: v.sender_name,
      sender_company: v.sender_company,
      sender_phone: v.sender_phone,
      sender_email: v.sender_email,
      receiver_name: v.receiver_name,
      receiver_company: v.receiver_company,
      contact_email: v.sender_email,
      goods_description: v.goods_description,
      vehicle_type: v.vehicle_type,
      vehicle_registration: v.vehicle_registration,
      driver_phone: v.driver_phone,
      notes: v.notes,
      tos_accepted_version: tos ? tos.version : null,
    };
    setBusy(true);
    const { data, error } = await submitPublicBooking(link.slug, payload);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (row?.id && declarations.length) {
      const { error: decErr } = await setBookingDeclarations(row.id, declAnswers, link.slug);
      if (decErr) toast.error(`Declarations not saved: ${decErr.message}`);
    }
    if (unticked.length) toast.warning(`To confirm on arrival before site access: ${unticked.join(", ")}`);
    onSubmitted(row || { reference_number: "—" });
  };

  return (
    <form onSubmit={submit} className="space-y-3 pt-4" data-testid="public-booking-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Direction" required>
          <Select value={v.direction} onValueChange={(x) => set("direction", x)}>
            <SelectTrigger data-testid="pb-direction"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(link.allowed_directions || ["inbound", "outbound"]).map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Vehicle type">
          <Input value={v.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)} data-testid="pb-vehicle" />
        </Field>
        <Field label="Date" required>
          <div className="flex gap-2">
            <AuDateInput required value={v.date} onChange={(e) => setV((s) => ({ ...s, date: e.target.value, start_ms: null }))} data-testid="pb-date" />
            <Button type="button" variant="outline" onClick={findNext} disabled={searching} data-testid="pb-next-available">
              {searching ? "Searching…" : "Next available"}
            </Button>
          </div>
        </Field>
        <Field label="Duration" required>
          <Select value={String(duration)} onValueChange={(x) => setV((s) => ({ ...s, duration_mins: parseInt(x, 10), start_ms: null }))}>
            <SelectTrigger data-testid="pb-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              {durations.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        {pickBay && (
          <Field label="Bay" required>
            <Select value={v.bay_id} onValueChange={(x) => setV((s) => ({ ...s, bay_id: x, start_ms: null }))}>
              <SelectTrigger data-testid="pb-bay"><SelectValue placeholder="Choose a bay" /></SelectTrigger>
              <SelectContent>
                {avail.bays.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`pb-bay-${b.id}`}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}
        <Field required label={`Start time${avail?.site ? ` (${placeTimeLabel({ ...avail.site, name: link.site?.name || avail.site.name }, v.date ? `${v.date}T12:00:00Z` : null)})` : ""}`}>
          {avail?.site && (
            <SiteClockLine tz={rules?.tz || avail.site.timezone || "Australia/Melbourne"} stepMin={rules?.slot || 30}
                           leadH={rules?.leadH ?? avail.site.min_lead_time_hours ?? null} />
          )}
          {!avail ? (
            <div className="text-xs text-slate-500 py-2" data-testid="pb-times-loading">Checking availability…</div>
          ) : avail.error ? (
            <div className="text-xs text-rose-600 py-2" data-testid="pb-avail-error">
              Couldn&apos;t load availability.{" "}
              <button type="button" className="underline" onClick={() => setReload((n) => n + 1)} data-testid="pb-avail-retry">Retry</button>
            </div>
          ) : pickBay && !v.bay_id ? (
            <div className="text-xs text-slate-500 py-2" data-testid="pb-pick-bay-first">Choose a bay to see its available times.</div>
          ) : starts.length === 0 ? (
            <div className="text-xs text-amber-700 py-2" data-testid="pb-no-times">No available times on this date — try another day.</div>
          ) : (
            <Select value={v.start_ms ? String(v.start_ms) : ""} onValueChange={(x) => set("start_ms", Number(x))}>
              <SelectTrigger data-testid="pb-time"><SelectValue placeholder={`${starts.length} times available`} /></SelectTrigger>
              <SelectContent>
                {starts.map((ms) => <SelectItem key={ms} value={String(ms)} data-testid={`pb-time-${fmtStart(ms).replace(":", "")}`}>{fmtStart(ms)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Vehicle registration" required={req("rego_prime_mover")}>
          <Input value={v.vehicle_registration} onChange={(e) => set("vehicle_registration", e.target.value)} data-testid="pb-rego" />
        </Field>
      </div>

      <div className="border-t pt-3" />
      <h3 className="text-sm font-semibold text-slate-700">Sender</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Contact name" required={!v.sender_company || req("sender_name")} hint={req("sender_name") ? "" : "or Company"}>
          <Input value={v.sender_name} onChange={(e) => set("sender_name", e.target.value)} data-testid="pb-sender-name" />
        </Field>
        <Field label="Company" required={req("sender_company")}>
          <Input value={v.sender_company} onChange={(e) => set("sender_company", e.target.value)} data-testid="pb-sender-company" />
        </Field>
        <Field label="Phone" required={req("sender_phone")}>
          <Input value={v.sender_phone} onChange={(e) => set("sender_phone", e.target.value)} data-testid="pb-sender-phone" />
        </Field>
        <Field label="Email" required={req("sender_email")}>
          <Input type="email" value={v.sender_email} onChange={(e) => set("sender_email", e.target.value)} data-testid="pb-sender-email" />
        </Field>
      </div>

      <div className="border-t pt-3" />
      <h3 className="text-sm font-semibold text-slate-700">Driver</h3>
      <Field label="Driver phone">
        <Input value={v.driver_phone} onChange={(e) => set("driver_phone", e.target.value)} data-testid="pb-driver-phone" />
      </Field>

      <div className="border-t pt-3" />
      <Field label="Goods description" required={req("goods_description")}>
        <Textarea rows={2} value={v.goods_description} onChange={(e) => set("goods_description", e.target.value)} data-testid="pb-goods" />
      </Field>
      <Field label="Notes">
        <Textarea rows={2} value={v.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pb-notes" />
      </Field>

      {tos && (
        <div className="pt-3 border-t" data-testid="public-tos-block">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            Terms of Service{" "}
            <span className="text-[11px] font-normal text-slate-500">(v{tos.version})</span>
          </h3>
          <ScrollArea className="h-40 rounded-md border border-slate-200 bg-slate-50 p-3">
            <TosMarkdown text={tos.markdown} className="text-xs text-slate-700" testid="public-tos-text" />
          </ScrollArea>
          <label className="mt-2 flex items-start gap-2 text-sm text-slate-700">
            <Checkbox
              checked={tosAccepted}
              onCheckedChange={(c) => setTosAccepted(!!c)}
              data-testid="public-tos-checkbox"
              className="mt-0.5"
            />
            <span>
              I have read and accept the Terms of Service above. By submitting
              this booking I confirm the information is accurate.
            </span>
          </label>
        </div>
      )}

      <DeclarationsList list={declarations} answers={declAnswers} onChange={setDeclAnswers} notice={declNotice} testPrefix="pb-declaration" />

      <div className="pt-3 flex items-center justify-end">
        <Button type="submit" disabled={busy || (tos && !tosAccepted)} data-testid="pb-submit">
          {busy ? "Submitting…" : "Submit booking"}
        </Button>
      </div>
    </form>
  );
}

// Public-page inputs that map to booking-form fields a tenant can mark Required.
const PUBLIC_FIELD_LABELS = {
  sender_name: "Contact name", sender_company: "Company", sender_phone: "Phone",
  sender_email: "Email", goods_description: "Goods description", rego_prime_mover: "Vehicle registration",
};
const PUBLIC_VALUE_KEY = { rego_prime_mover: "vehicle_registration" };

function usePublicRequired(slug) {
  const [keys, setKeys] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    publicRequiredFields(slug).then(({ data, error }) => {
      if (!cancelled && !error) setKeys(new Set((data || []).map((r) => (typeof r === "string" ? r : r.lbs_public_required_fields))));
    });
    return () => { cancelled = true; };
  }, [slug]);
  return keys;
}

function Field({ label, required, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">
        {label}
        {required && <span className="text-rose-600 ml-0.5" title="Required field">*</span>}
        {required && hint && <span className="ml-1 normal-case tracking-normal text-slate-400">({hint})</span>}
      </Label>
      {children}
    </div>
  );
}

function Frame({ children, brand }) {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <TenantLogo brand={brand} className="mb-4" />
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <span className="font-semibold text-slate-900">timeslot.au</span>
          {brand?.name && <><span>·</span><span data-testid="public-tenant-name">{brand.name}</span></>}
        </div>
        <Card>
          <CardContent className="p-6">{children}</CardContent>
        </Card>
        <p className="text-[11px] text-center text-slate-400 mt-3">
          Powered by timeslot.au · Booking subject to approval by the receiving site.
        </p>
      </div>
    </div>
  );
}
